import {
  da,
  de,
  deAT,
  enGB,
  enUS,
  es,
  fi,
  fr,
  frCH,
  hu,
  it,
  itCH,
  nl,
  pl,
  pt,
  ptBR,
  sk,
  vi
} from 'date-fns/locale';
import { parse, parseISO } from 'date-fns';
import type { DateFormatter } from './DateFormatter';

// NOTE: any new locales need to be added here!
const locales = {
  'de-AT': deAT,
  'en-GB': enGB,
  'en-US': enUS,
  'fr-CH': frCH,
  'it-CH': itCH,
  'pt-BR': ptBR,
  da,
  de,
  en: enUS,
  es,
  fi,
  fr,
  hu,
  it,
  nl,
  pl,
  pt,
  sk,
  vi
};

const currentLocale = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  let locale = locales[window.SenseConstants?.locale ?? ''];
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (!locale) locale = locales[window.SenseConstants?.language ?? ''];
  return locale ?? locales['en'];
};

/**
 * (more or less) safely parse a user-input date.
 *
 * This will attempt to parse it like {@link DefaultDateFormatter} would format it, in the users' locale.
 * ISO strings will also work, in order to support Dates from AJAX calls.
 * Already-parsed Date objects are passed through.
 *
 * @param input date as string or object
 * @author Bennett Piater
 */
export function parseDate(input: Date | string): Date {
  // Dates are passed through
  if (typeof input === 'object') {
    return input;
  }

  // always be capable of parsing ISO strings
  const parsedAsISO = parseISO(input);
  if (!isNaN(parsedAsISO.valueOf())) return parsedAsISO;

  // Parse in UTC to have 0 UTC hours. This *should* work fine with our (de)serializtion, but flatpickr and yup need to be checked.
  return parse(input + 'Z', 'PX', new Date(), { locale: currentLocale() });
}

/**
 * Detect based upon format option.
 * https://tc39.es/ecma402/#sec-datetimeformat-abstracts
 * https://date-fns.org/v2.29.3/docs/parse
 *
 * 1 = M
 * 01 = MM
 * J = MMMMM
 * Jan. = MMM
 * January = MMMM
 *
 * Not supported
 * 1st = Mo
 * @param option {@link Intl.DateTimeFormat} format option
 */
function detectMonthFromFormatOption(option: string | undefined): string {
  switch (option) {
    case 'numeric':
      return 'M';
    case '2-digit':
      return 'MM';
    case 'narrow':
      return 'MMMMM';
    case 'short':
      return 'MMM';
    case 'long':
      return 'MMMM';
    default:
      return 'MM';
  }
}

/**
 * Get a Date formatting string (eg. dd/MM/YYYY, HH:mm) based on {@link Intl.DateTimeFormat} options.
 * Supported:<br>
 * <li>month: numeric, 2-digit, long, short, narrow</li>
 * <li>day: numeric, 2-digit</li>
 * <li>year: numeric, 2-digit</li>
 * <li>hour: numeric, 2-digit</li>
 * <li>minute: numeric, 2-digit</li>
 * <li>second: numeric, 2-digit</li>
 *
 * DISCLAIMER!
 * Does not support every possible setting!!
 * Time should not have hourCycle h11, h12, since AM, PM parsing is not correctly implemented. (You would get a warning that type dayPeriod is not supported).
 *
 * @param options the options.
 */
export function getDateFormattingString(options: Intl.DateTimeFormat): string {
  return options
    .formatToParts()
    .map(({ type, value }) => {
      switch (type) {
        case 'month':
          return detectMonthFromFormatOption(options.resolvedOptions().month);
        case 'day':
        case 'year':
        case 'minute':
        case 'second':
          return type[0].repeat(value.length);
        case 'hour':
          return 'H'.repeat(value.length);
        case 'literal':
          return value;
        default:
          console.warn('Not supported DateTime formatting part: %s', type);
          return '';
      }
    })
    .join('');
}

/**
 * Format function for {@link getDateFormattingString}.
 *
 * @param locale instance of {@link DateFormatter#locale}
 * @param options instance of {@link DateFormatter#options}
 */
export function getLocaleDateFormat({
  locale,
  options
}: DateFormatter): string {
  return getDateFormattingString(new Intl.DateTimeFormat(locale, options));
}

/**
 * Takes a string and a makes a date formatting string out of it (e.g. 01/01/2000, 11:00 => dd/MM/yyyy, HH:mm).<br>
 * First it always tries to parse UTC.<br>
 * If you do not provide a date formatter the date constructor will be used for parsing.<br>
 * In case of inability to parse something now will be returned as fallback.
 *
 * @param date the date
 * @param options the formatting options used to create the string provided as the first parameter
 */
export function parseDateWithOptions(
  date: string,
  options?: DateFormatter
): Date {
  try {
    // always be capable of parsing ISO strings
    // The datepicker on mobile phones formats everything by himself and then returns UTC conform date string
    console.debug('Date input: %s', date);
    const parsedAsISO = parseISO(date);

    if (!isNaN(parsedAsISO.valueOf())) return parsedAsISO;

    if (!options) {
      return new Date(date);
    }

    const now = new Date();
    const dateFormattingString = getLocaleDateFormat(options);
    const locale = locales[options.locale as keyof typeof locales] ?? enGB;
    console.debug(
      'The value %s will be parsed with %s. Locale %s',
      date,
      dateFormattingString,
      locale.code
    );
    const result = parse(date, dateFormattingString, now, { locale }) ?? now;
    console.debug('The date formatting string is: %s', result);

    if (Number.isNaN(result.getUTCDate())) {
      console.debug('The date could not be parsed return now as fallback');
      return now;
    }

    return result;
  } catch (e: unknown) {
    console.error('Parsing a date yielded an error: %s', e);
    return new Date();
  }
}
